// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).
//
// =============================================================================
// CaseworkerEditForm.jsx — the caseworker edit form (Phase 3 relocation)
// =============================================================================
// Extracted VERBATIM from UserAdminCaseworkerList's inline edit branch when the
// standalone Caseworkers tab was retired (Phase 3 of the shelter-admin tabbed
// redesign): the form now renders inline under a user's row on the shelter
// detail's Users tab. Same state contract as before — UserAdmin owns
// editing/form (startEdit seeds it); saveEdit persists profile + memberships.

import { formatPhone, isValidEmail } from "../../utils/sanitize";
import { Btn } from "../../utils/ui.jsx";

// The structured Contact Role options. A contact imported from Unleashed can carry a
// free-text role in contact_label (e.g. "Crisi Accommodation") that ISN'T one of
// these — the dropdown surfaces that value as its own option so it's visible and
// can't be silently overwritten on Save (cleanup = deliberately picking a standard
// one). contact_label is portal-only; it is never pushed to Unleashed.
const STANDARD_CONTACT_ROLES = [
  "DFV Service Manager",
  "Caseworker",
  "Admin Officer",
  "Coordinator",
  "Other",
];

export function CaseworkerEditForm({
  user, // the profile row being edited (ids for label wiring)
  form,
  setForm,
  fv,
  setF,
  saving,
  saveEdit,
  sendPasswordReset,
  shelters,
  // The service this edit was opened from — the Contact Role is per-service, so we
  // name it on the field to remove any ambiguity about which service's role you're
  // seeing (e.g. "Contact Role — GGGG-GGGG (GGGGG)").
  serviceLabel,
  // Contact-model No-Login: true when editing a login-less contact (a user_shelters
  // row with no profile). It belongs to exactly one service, so the multi-service +
  // Primary-service pickers are hidden and Save updates only its membership.
  noLoginContact = false,
  // Read-only Unleashed Contact GUID shown in the footer (debug aid — the portal exposed
  // it nowhere else). null / "" when the contact isn't linked to Unleashed.
  contactGuid = null,
  onCancel,
}) {
  const u = user;
  // Block Save on an unsendable address (email-validation-plan §6A). Blank is left to
  // the existing save rules (a login-less contact may have no email).
  const emailValue = String(fv(form, "user_email") || "");
  const emailInvalid = emailValue.trim() !== "" && !isValidEmail(emailValue);
  return (
    <div className="py-[14px] px-[18px]">
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label
            htmlFor={`cw-first-name-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            First Name
          </label>
          <input
            id={`cw-first-name-${u.id}`}
            value={fv(form, "first_name")}
            onChange={function (e) {
              setF("first_name", e.target.value);
            }}
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          />
        </div>
        <div>
          <label
            htmlFor={`cw-last-name-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            Last Name
          </label>
          <input
            id={`cw-last-name-${u.id}`}
            value={fv(form, "last_name")}
            onChange={function (e) {
              setF("last_name", e.target.value);
            }}
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          />
        </div>
        <div>
          <label
            htmlFor={`cw-email-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            {noLoginContact ? "Contact Email" : "Login Email"}{" "}
            <span className="text-[10px] text-zephyr-muted font-normal normal-case">
              {noLoginContact
                ? "(used if you create a login)"
                : "(changing this updates the login email)"}
            </span>
          </label>
          <input
            id={`cw-email-${u.id}`}
            type="email"
            value={fv(form, "user_email")}
            onChange={function (e) {
              setF("user_email", e.target.value);
            }}
            title="Saving with a different email will update both auth.users and profiles via the admin-users Edge Function. The user will need to log in with the new email."
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          />
          {emailInvalid && (
            <p role="alert" className="text-[11px] text-zephyr-red mt-1 mb-0">
              Not a valid email address — please check it.
            </p>
          )}
        </div>
        {!noLoginContact && (
          <div>
            <label
              htmlFor={`cw-shelter-${u.id}`}
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Primary DFV Service
            </label>
            <select
              id={`cw-shelter-${u.id}`}
              value={fv(form, "shelter_code")}
              onChange={function (e) {
                setF("shelter_code", e.target.value);
              }}
              className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
              style={{ fontFamily: "inherit" }}
            >
              <option value="">— Select a DFV Service —</option>
              {shelters.map(function (s) {
                return (
                  <option key={s.shelter_code} value={s.shelter_code}>
                    {s.shelter_name} ({s.shelter_code})
                  </option>
                );
              })}
            </select>
          </div>
        )}
        <div>
          <label
            htmlFor={`cw-contact-label-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            Contact Role{" "}
            <span className="text-[10px] text-zephyr-muted font-normal normal-case">
              {serviceLabel ? `— ${serviceLabel} only` : "(this DFV Service only)"}
            </span>
          </label>
          <select
            id={`cw-contact-label-${u.id}`}
            value={fv(form, "contact_label")}
            onChange={function (e) {
              setF("contact_label", e.target.value);
            }}
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          >
            {/* Imported free-text role (e.g. "Crisi Accommodation") — shown so it's
                visible and preserved until deliberately changed during cleanup. */}
            {(() => {
              const cur = fv(form, "contact_label");
              return cur && !STANDARD_CONTACT_ROLES.includes(cur) ? (
                <option value={cur}>{cur} (imported)</option>
              ) : null;
            })()}
            <option value="DFV Service Manager">DFV Service Manager</option>
            <option value="Caseworker">Caseworker</option>
            <option value="Admin Officer">Admin Officer</option>
            <option value="Coordinator">Coordinator</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label
            htmlFor={`cw-phone-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            Phone
          </label>
          <input
            id={`cw-phone-${u.id}`}
            value={fv(form, "phone")}
            autoComplete="off"
            onChange={function (e) {
              setF("phone", e.target.value);
            }}
            onBlur={function (e) {
              setF("phone", formatPhone(e.target.value, "landline"));
            }}
            placeholder="e.g. 07 3219 5644"
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          />
        </div>
        <div>
          <label
            htmlFor={`cw-mobile-${u.id}`}
            className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
          >
            Mobile
          </label>
          <input
            id={`cw-mobile-${u.id}`}
            value={fv(form, "mobile")}
            autoComplete="off"
            onBlur={function (e) {
              setF("mobile", formatPhone(e.target.value, "mobile"));
            }}
            onChange={function (e) {
              setF("mobile", e.target.value);
            }}
            placeholder="e.g. 0412 345 678"
            className="w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
            style={{ fontFamily: "inherit" }}
          />
        </div>
      </div>
      <div className="text-[11px] text-zephyr-muted mb-3">
        Name, Login Email, Phone and Mobile are the person's contact details — editing them updates
        this person across <b>every</b> DFV Service they belong to. Contact Role is per-service.
      </div>
      {!noLoginContact && (
        <div className="mb-3">
          <label className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1">
            Additional DFV Services (multi-service access)
          </label>
          <div className="flex flex-wrap gap-[6px] mb-2">
            {(form.shelter_codes || [])
              .filter(function (code) {
                return code !== fv(form, "shelter_code");
              })
              .map(function (code) {
                const shelter = shelters.find(function (s) {
                  return s.shelter_code === code;
                });
                return (
                  <span
                    key={code}
                    className="inline-flex items-center gap-[6px] rounded-full py-[3px] px-[10px] text-[12px] bg-zephyr-teal-light border border-zephyr-teal"
                  >
                    {shelter ? shelter.shelter_name : code} ({code})
                    <button
                      onClick={function () {
                        setForm(function (f) {
                          return Object.assign({}, f, {
                            shelter_codes: (f.shelter_codes || []).filter(function (c) {
                              return c !== code;
                            }),
                          });
                        });
                      }}
                      className="bg-transparent border-0 cursor-pointer text-zephyr-red font-bold text-[14px] p-0 leading-none"
                    >
                      &times;
                    </button>
                  </span>
                );
              })}
          </div>
          <select
            value=""
            onChange={function (e) {
              if (!e.target.value) return;
              const code = e.target.value;
              setForm(function (f) {
                const existing = f.shelter_codes || [];
                if (existing.indexOf(code) === -1) {
                  return Object.assign({}, f, {
                    shelter_codes: existing.concat([code]),
                  });
                }
                return f;
              });
              e.target.value = "";
            }}
            className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white max-w-[320px]"
            style={{ fontFamily: "inherit" }}
          >
            <option value="">+ Add another DFV Service...</option>
            {shelters
              .filter(function (s) {
                return (form.shelter_codes || []).indexOf(s.shelter_code) === -1;
              })
              .map(function (s) {
                return (
                  <option key={s.shelter_code} value={s.shelter_code}>
                    {s.shelter_name} ({s.shelter_code})
                  </option>
                );
              })}
          </select>
          <div className="text-[11px] text-zephyr-muted mt-1">
            Multi-service users choose which DFV Service to work with at login.
          </div>
        </div>
      )}
      {!noLoginContact && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input
              type="checkbox"
              checked={fv(form, "is_generic") || false}
              onChange={function (e) {
                setF("is_generic", e.target.checked);
              }}
            />
            <span>Shared / generic account</span>
            <span className="text-[11px] text-zephyr-muted">(no named individual)</span>
          </label>
        </div>
      )}
      {/* Contact-model No-Login: tick on a login user to REMOVE their login (they
          become a contact-only record across all their services); un-tick on a
          login-less contact to CREATE a login account for them. */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-[13px] cursor-pointer">
          <input
            type="checkbox"
            checked={fv(form, "no_login") === true}
            onChange={function (e) {
              setF("no_login", e.target.checked);
            }}
          />
          <span>No login account (contact only)</span>
        </label>
        <div className="text-[11px] text-zephyr-muted mt-1 ml-6">
          {noLoginContact
            ? "Un-tick + Save to create a login account for this person (they'll be invited from Manage → Invitations)."
            : "Tick + Save to remove this person's login — they become a contact-only record across every DFV Service they belong to."}
        </div>
      </div>
      <div className="flex gap-[10px] items-center">
        <Btn variant="secondary" onClick={onCancel}>
          Cancel
        </Btn>
        {!noLoginContact && (
          <Btn
            onClick={function () {
              sendPasswordReset(fv(form, "user_email"));
            }}
            variant="secondary"
          >
            📧 Password Reset Email
          </Btn>
        )}
        <Btn onClick={saveEdit} disabled={saving || emailInvalid}>
          {saving ? "Saving..." : noLoginContact ? "Save Contact" : "Save User"}
        </Btn>
        {contactGuid && (
          <span
            className="ml-auto text-[10px] text-zephyr-muted font-mono select-all"
            title="Unleashed Contact GUID (read-only)"
          >
            Unleashed GUID: {contactGuid}
          </span>
        )}
      </div>
    </div>
  );
}
